import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, ShieldCheck, TrendingUp, TrendingDown, Rocket, Clock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import Navbar from "../components/Navbar";
import CustomPortfolioPicker from "../components/CustomPortfolioPicker";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Portfolio — the compliance-safe hero page.
 *
 * Answers ONE question honestly: "If a subscriber started with $10,000 90 days
 * ago and followed every alert with the disclosed rules, where would they be
 * today?" Then it shows the math, every trade, every skipped alert, and the
 * disclosure block so nothing is hidden.
 *
 * Public route — this is our best marketing asset AND our audit trail.
 */
export default function Portfolio() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tradesOpen, setTradesOpen] = useState(false);
  const [skippedOpen, setSkippedOpen] = useState(false);
  // When a subscriber has a saved custom scenario (or runs one now), the
  // hero cards + curve chart switch to their picks. Reset in the picker
  // clears this back to the canonical published number.
  const [customResult, setCustomResult] = useState(null);

  useEffect(() => {
    // 25s hard timeout so a stalled backend can't leave the page on an
    // infinite spinner — we surface a retry button instead. Fixes the
    // "page hangs forever" symptom users reported when the event loop
    // was pinned on background LLM traffic.
    const load = () => {
      setLoadError(null);
      return axios
        .get(`${BACKEND_URL}/api/portfolio`, { timeout: 25000 })
        .then((r) => setData(r.data))
        .catch((e) => setLoadError(e?.message || "Couldn't load portfolio"));
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="bg-mb-sand min-h-screen">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 flex flex-col items-center gap-4">
          {loadError ? (
            <>
              <p className="text-sm text-red-600" data-testid="portfolio-load-error">Couldn&apos;t load the simulation — {loadError}</p>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-md bg-mb-emerald text-white text-sm">Retry</button>
            </>
          ) : (
            <Loader2 className="w-8 h-8 animate-spin text-mb-emerald" />
          )}
        </div>
      </div>
    );
  }

  const isWarming = data.warming;
  // If the subscriber has a saved custom scenario, the hero + curve reflect
  // THEIR picks; everything else (trades table, skipped list, assumptions,
  // disclaimer) stays on the canonical data so the audit trail is intact.
  const view = customResult || data;
  const isCustom = !!customResult;
  const isWin = view.return_pct >= 0;
  const beatsSpy = view.return_pct - (view.spy_return_pct || 0);
  // "X times the market" is meaningful only when SPY finished the window
  // positive AND we finished positive — otherwise the ratio is misleading
  // (negative denominators, "less bad" spins, etc.). Guard the denominator
  // too so a near-flat SPY doesn't produce a 30× fireworks number that
  // would look absurd. The vs-SPY pp figure covers every other case.
  const marketMultiple = (view.spy_return_pct > 0.5 && view.return_pct > 0)
    ? (view.return_pct / view.spy_return_pct)
    : null;

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        {/* Header */}
        <div className="mb-6">
          <div className="label-uppercase text-mb-emerald flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Compliance-safe portfolio simulation
          </div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mt-1" data-testid="portfolio-title">
            $10,000 → what you&apos;d have today
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            If you started with $10,000 exactly {view.window_days} days ago and followed every Moonbucks alert with realistic retail rules,
            here&apos;s where you&apos;d be — and here&apos;s every trade to prove it.
          </p>
          {isCustom && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-300 text-[11px] text-amber-800" data-testid="portfolio-custom-active">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Showing YOUR custom picks ({customResult.picked_count} alert{customResult.picked_count === 1 ? "" : "s"}). Reset in the picker below to see the canonical number.
            </div>
          )}
        </div>

        {/* Hero numbers */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <HeroStat label={isCustom ? "Ending balance (your picks)" : "Ending balance"} value={`$${view.final_equity.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`} testid="portfolio-final" accent="emerald" />
          <HeroStat
            label={isCustom ? "Return (your picks)" : "Return"}
            value={`${isWin ? "+" : ""}${view.return_pct.toFixed(2)}%`}
            sub={marketMultiple ? `${marketMultiple.toFixed(1)}× the market` : null}
            testid="portfolio-return"
            accent={isWin ? "emerald" : "red"}
          />
          <HeroStat
            label={`vs SPY (${view.spy_return_pct >= 0 ? "+" : ""}${view.spy_return_pct}% same window)`}
            value={`${beatsSpy >= 0 ? "+" : ""}${beatsSpy.toFixed(2)}pp`}
            testid="portfolio-vs-spy"
            accent={beatsSpy >= 0 ? "emerald" : "amber"}
          />
        </div>

        {/* Curve */}
        {view.curve && view.curve.length > 1 && (
          <div className="bg-white border border-mb-border rounded-lg p-4 mb-6" data-testid="portfolio-curve">
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={view.curve}>
                  <defs>
                    <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#eef2f0" strokeDasharray="2 4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} domain={["dataMin - 50", "dataMax + 50"]} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
                  <Tooltip
                    formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === "equity" ? (isCustom ? "Your picks" : "Portfolio") : name]}
                    labelStyle={{ fontSize: 11 }}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <ReferenceLine y={view.seed} stroke="#9ca3af" strokeDasharray="4 4" label={{ value: `Seed $${view.seed}`, fill: "#6b7280", fontSize: 10 }} />
                  <Area
                    type="stepAfter"
                    dataKey="equity"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#eq)"
                    dot={{ r: 2, fill: "#10B981", stroke: "#10B981", strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: "#ffffff", stroke: "#10B981", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {isWarming && (
          <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="portfolio-warming">
            The portfolio numbers are being recomputed — check back in a minute for the latest.
          </div>
        )}

        {/* Custom scenario picker — subscribers can hand-pick alerts and
            re-run the sim to see "what would MY picks have delivered". */}
        <CustomPortfolioPicker user={user} onResult={setCustomResult} />

        {/* Assumptions */}
        <section className="mb-6" data-testid="portfolio-assumptions">
          <h2 className="font-display font-bold text-xl mb-3">The rules of the simulation</h2>
          <div className="bg-white border border-mb-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {(data.assumptions || []).map((a, i) => (
                  <tr key={a.key} className={i > 0 ? "border-t border-mb-border/50" : ""}>
                    <td className="px-4 py-2.5 text-neutral-600 w-1/3">{a.label}</td>
                    <td className="px-4 py-2.5 font-mono-data text-xs text-neutral-900">{a.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Roll-up */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <MiniStat label="Alerts published" value={data.alerts_published} testid="portfolio-published" />
          <MiniStat label="You'd have taken" value={data.alerts_followed} testid="portfolio-followed" />
          <MiniStat label="Wins" value={data.wins} testid="portfolio-wins" accent="emerald" />
          <MiniStat label="Losses" value={data.losses} testid="portfolio-losses" accent="red" />
        </div>

        {/* Trades table */}
        <section className="mb-6" data-testid="portfolio-trades-section">
          <button
            onClick={() => setTradesOpen(!tradesOpen)}
            className="w-full flex justify-between items-center py-3 px-4 bg-white border border-mb-border rounded-lg hover:bg-neutral-50"
            data-testid="portfolio-trades-toggle"
          >
            <div>
              <h2 className="font-display font-bold text-xl text-left">Every trade — {data.trades?.length || 0} closed</h2>
              <p className="text-xs text-neutral-500 text-left">Click to {tradesOpen ? "collapse" : "audit every position"}</p>
            </div>
            <span className="text-mb-emerald text-sm">{tradesOpen ? "−" : "+"}</span>
          </button>
          {tradesOpen && (
            <div className="mt-3 bg-white border border-mb-border rounded-lg overflow-hidden overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 label-uppercase text-[10px] text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-2">Symbol</th>
                    <th className="text-left px-3 py-2">Class</th>
                    <th className="text-left px-3 py-2">In → Out</th>
                    <th className="text-right px-3 py-2">Days</th>
                    <th className="text-right px-3 py-2">Net %</th>
                    <th className="text-right px-3 py-2">$ P&amp;L</th>
                    <th className="text-left px-3 py-2">Why exit</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.trades || []).map((t) => (
                    <tr key={t.id} className="border-t border-mb-border/40" data-testid={`portfolio-trade-${t.symbol}`}>
                      <td className="px-3 py-2 font-mono-data font-bold">{t.symbol}</td>
                      <td className="px-3 py-2 text-neutral-500">{t.alert_class}</td>
                      <td className="px-3 py-2 font-mono-data text-neutral-600">{t.entered_on} → {t.exited_on}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.days_held ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-data font-bold tabular-nums" style={{ color: t.net_pct >= 0 ? "#10B981" : "#EF4444" }}>
                        {t.net_pct >= 0 ? "+" : ""}{t.net_pct.toFixed(2)}%
                      </td>
                      <td className="px-3 py-2 text-right font-mono-data tabular-nums" style={{ color: t.dollars_pnl >= 0 ? "#10B981" : "#EF4444" }}>
                        {t.dollars_pnl >= 0 ? "+" : ""}${t.dollars_pnl.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-neutral-600 text-[11px]">{t.why_exit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Skipped */}
        {data.skipped && data.skipped.length > 0 && (
          <section className="mb-6" data-testid="portfolio-skipped-section">
            <button
              onClick={() => setSkippedOpen(!skippedOpen)}
              className="w-full flex justify-between items-center py-3 px-4 bg-white border border-mb-border rounded-lg hover:bg-neutral-50"
              data-testid="portfolio-skipped-toggle"
            >
              <div>
                <h2 className="font-display font-bold text-lg text-left">
                  We skipped {data.skipped.length} alert{data.skipped.length === 1 ? "" : "s"} (all {data.assumptions?.find(a=>a.key==="slots")?.value.split(" ")[0] || "4"} slots were full)
                </h2>
                <p className="text-xs text-neutral-500 text-left">Honest — we don&apos;t cherry-pick. Click to see which ones.</p>
              </div>
              <span className="text-mb-emerald text-sm">{skippedOpen ? "−" : "+"}</span>
            </button>
            {skippedOpen && (
              <div className="mt-3 bg-white border border-mb-border rounded-lg overflow-hidden">
                <ul className="divide-y divide-mb-border/40">
                  {data.skipped.map((s) => (
                    <li key={s.id} className="px-3 py-2 flex justify-between items-center" data-testid={`portfolio-skipped-${s.symbol}`}>
                      <span className="font-mono-data font-bold">{s.symbol}</span>
                      <span className="text-xs text-neutral-500">{s.date} · {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Disclaimer */}
        <section className="mt-8 p-4 rounded-lg bg-neutral-100 border border-mb-border text-[11px] leading-relaxed text-neutral-600" data-testid="portfolio-disclaimer">
          <div className="label-uppercase text-neutral-500 mb-1">Disclosures</div>
          {data.disclaimer}
          {data.computed_at && (
            <div className="mt-2 font-mono-data text-[10px] text-neutral-400">
              Last recomputed: {new Date(data.computed_at).toLocaleString()}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const HeroStat = ({ label, value, accent, testid, sub }) => (
  <div
    className={`bg-white rounded-lg p-4 border ${accent === "emerald" ? "border-mb-emerald" : accent === "red" ? "border-red-400" : accent === "amber" ? "border-amber-400" : "border-mb-border"}`}
    data-testid={testid}
  >
    <div className="label-uppercase text-[10px] text-neutral-500">{label}</div>
    <div className={`mt-1 font-mono-data text-3xl sm:text-4xl font-extrabold ${accent === "emerald" ? "text-mb-emerald" : accent === "red" ? "text-red-600" : accent === "amber" ? "text-amber-600" : "text-neutral-900"}`}>
      {value}
    </div>
    {sub && (
      <div
        className={`mt-1 font-mono-data text-xs font-semibold ${accent === "emerald" ? "text-mb-emerald/80" : "text-neutral-600"}`}
        data-testid={testid ? `${testid}-sub` : undefined}
      >
        {sub}
      </div>
    )}
  </div>
);

const MiniStat = ({ label, value, accent, testid }) => (
  <div className="bg-white border border-mb-border rounded-lg p-3" data-testid={testid}>
    <div className="label-uppercase text-[10px] text-neutral-500">{label}</div>
    <div className={`mt-0.5 font-mono-data text-xl font-bold ${accent === "emerald" ? "text-mb-emerald" : accent === "red" ? "text-red-600" : "text-neutral-900"}`}>{value}</div>
  </div>
);
